import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	Button,
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { useSelector } from "react-redux";
import moment from "moment";
import { useRouter } from "utils";
import { useCurrentRole } from "utils/current-user";
import { canManageOrganisationRecords } from "utils/roles";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { EQUIPMENT_USAGE_EDIT_ROUTE } from "../actions/types";
import { formatLinkedUsageSource } from "../utils/equipment-usage-display";
import { unitLabel } from "../utils/equipment-usage-math";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		...theme.typography.body1,
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	details: {
		padding: theme.spacing(0, 0, 2, 0),
		display: "block",
		overflowX: "auto",
	},
	empty: {
		padding: theme.spacing(2),
		color: theme.palette.text.secondary,
	},
	recordButton: {
		margin: theme.spacing(0, 2, 1, 2),
	},
	actionsCell: {
		whiteSpace: "nowrap",
	},
}));

const formatReading = (value) =>
	value === null || value === undefined || value === "" ? "—" : value;

const EquipmentUsagePanel = ({
	equipmentId,
	expanded,
	onExpandedChange,
	onRecordUsage,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const role = useCurrentRole();
	const isAdmin = canManageOrganisationRecords(role);
	const organisationId = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);

	const load = useCallback(() => {
		if (!organisationId || !equipmentId) {
			setRows([]);
			return;
		}
		setLoading(true);
		safetyWiseApi
			.get(`${organisationId}/equipment-usage/for-equipment/${equipmentId}`)
			.then((res) => setRows(Array.isArray(res.data) ? res.data : []))
			.catch(() => setRows([]))
			.finally(() => setLoading(false));
	}, [organisationId, equipmentId]);

	useEffect(() => {
		if (expanded) {
			load();
		}
	}, [expanded, load]);

	const onDelete = async (id) => {
		if (!window.confirm("Delete this usage record?")) return;
		try {
			await safetyWiseApi.del(`${organisationId}/equipment-usage/delete/${id}`);
			load();
		} catch (err) {
			window.alert(
				err?.response?.data?.message || "Unable to delete usage record."
			);
		}
	};

	const onEdit = (id) => {
		history.push(
			EQUIPMENT_USAGE_EDIT_ROUTE.replace(":usageId", id),
			{ from: history.location.pathname }
		);
	};

	if (!equipmentId) return null;

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="equipment-usage-panel"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon />}
				className={classes.summary}
			>
				<Typography component="h2" className={classes.summaryTitle}>
					Usage
					<span className={classes.countMuted}>
						{rows.length ? ` (${rows.length})` : ""}
					</span>
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				{onRecordUsage && (
					<Button
						className={classes.recordButton}
						color="primary"
						variant="outlined"
						size="small"
						onClick={onRecordUsage}
					>
						Record usage
					</Button>
				)}
				{expanded && !loading && rows.length === 0 && (
					<Typography className={classes.empty}>
						No usage recorded yet.
					</Typography>
				)}
				{expanded && rows.length > 0 && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell>Staff</TableCell>
								<TableCell>Units</TableCell>
								<TableCell>Start</TableCell>
								<TableCell>End</TableCell>
								<TableCell>Linked</TableCell>
								<TableCell>Notes</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((row) => (
								<TableRow key={row.id}>
									<TableCell>
										{row.usageDate
											? moment(row.usageDate).format("D MMM YYYY")
											: "—"}
									</TableCell>
									<TableCell>{row.staffName || "—"}</TableCell>
									<TableCell>
										{row.unitsUsed} {unitLabel(row.unitType)}
									</TableCell>
									<TableCell>{formatReading(row.startReading)}</TableCell>
									<TableCell>{formatReading(row.endReading)}</TableCell>
									<TableCell>
										{formatLinkedUsageSource(row.source) || ""}
									</TableCell>
									<TableCell>{row.notes || "—"}</TableCell>
									<TableCell align="right" className={classes.actionsCell}>
										{isAdmin && (
											<IconButton
												size="small"
												aria-label="Edit usage"
												onClick={() => onEdit(row.id)}
											>
												<EditIcon fontSize="small" />
											</IconButton>
										)}
										{isAdmin && (
											<IconButton
												size="small"
												aria-label="Delete usage"
												onClick={() => onDelete(row.id)}
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

EquipmentUsagePanel.propTypes = {
	equipmentId: PropTypes.string,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
	onRecordUsage: PropTypes.func,
};

export default EquipmentUsagePanel;
